<template>
  <div class="admin-customer-pricing">
    <NavBar />
    <div class="container-fluid py-4 px-4">
      <div class="row">
        <div class="col-12">
          <div class="card shadow">
            <div class="card-header bg-white border-0">
              <h3 class="mb-0">Customer Pricing</h3>
            </div>
            <div class="card-body">
              <div v-if="loading" class="text-center py-5">
                <b-spinner variant="primary" />
                <p class="mt-2">Loading...</p>
              </div>
              <div v-else-if="error" class="text-center py-5">
                <p class="text-danger">{{ error }}</p>
                <b-button variant="primary" @click="fetchData">Retry</b-button>
              </div>
              <div v-else>
                <div class="table-responsive">
                  <table class="table table-hover align-middle">
                    <thead class="thead-light">
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Mode</th>
                        <th>Balance</th>
                        <th>Daily Price</th>
                        <th>Max Days</th>
                        <th>White Label</th>
                        <th>Currency</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="customer in customers" :key="customer.user_id">
                        <td>{{ customer.user_id }}</td>
                        <td>{{ customer.username || '—' }}</td>
                        <td>{{ customer.email || '—' }}</td>
                        <td>
                          <b-form-select
                            v-model="customer.billing_mode"
                            :options="['prepaid', 'postpaid']"
                            size="sm"
                            style="width: 100px;"
                          />
                        </td>
                        <td>
                          <b-form-input
                            v-model.number="customer.prepaid_balance"
                            type="number"
                            size="sm"
                            style="width: 90px;"
                            min="0"
                            step="0.01"
                          />
                        </td>
                        <td>
                          <b-form-input
                            v-model.number="customer.daily_price"
                            type="number"
                            size="sm"
                            style="width: 80px;"
                            min="0"
                            step="0.01"
                          />
                        </td>
                        <td>
                          <b-form-input
                            v-model.number="customer.max_charge_days_per_trip"
                            type="number"
                            size="sm"
                            style="width: 70px;"
                            min="0"
                            step="1"
                          />
                        </td>
                        <td>
                          <b-form-input
                            v-model.number="customer.white_label_price"
                            type="number"
                            size="sm"
                            style="width: 80px;"
                            min="0"
                            step="0.01"
                            placeholder="None"
                          />
                        </td>
                        <td>
                          <b-form-input
                            v-model="customer.currency"
                            size="sm"
                            style="width: 65px;"
                            maxlength="3"
                          />
                        </td>
                        <td>
                          <b-button
                            variant="primary"
                            size="sm"
                            :disabled="saving === customer.user_id"
                            @click="save(customer)"
                          >
                            <b-spinner v-if="saving === customer.user_id" small />
                            <span v-else>Save</span>
                          </b-button>
                          <div v-if="saveError[customer.user_id]" class="text-danger mt-1" style="font-size: 11px;">
                            {{ saveError[customer.user_id] }}
                          </div>
                          <div v-if="saveSuccess[customer.user_id]" class="text-success mt-1" style="font-size: 11px;">
                            Saved
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import NavBar from '@/components/NavBar';
import billingApi from '@/api/billing';

const INTERNAL_HOME_PATH = '/';

export default {
  name: 'AdminCustomerPricing',
  components: { NavBar },
  data() {
    return {
      loading: false,
      error: null,
      customers: [],
      saving: null,
      saveError: {},
      saveSuccess: {},
    };
  },
  computed: {
    ...mapGetters(['apptoken', 'userId']),
  },
  created() {
    if (this.userId !== 1) {
      this.$router.push(INTERNAL_HOME_PATH);
      return;
    }
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        const res = await billingApi.getCustomerPricing(this.apptoken);
        this.customers = res.data || [];
      } catch (e) {
        this.error = e.response?.data?.message || 'Failed to load pricing data.';
      } finally {
        this.loading = false;
      }
    },
    async save(customer) {
      this.saving = customer.user_id;
      this.$set(this.saveError, customer.user_id, null);
      this.$set(this.saveSuccess, customer.user_id, false);
      try {
        await billingApi.updateCustomerPricing(this.apptoken, customer.user_id, {
          daily_price: customer.daily_price,
          white_label_price: customer.white_label_price,
          max_charge_days_per_trip: customer.max_charge_days_per_trip,
          billing_mode: customer.billing_mode,
          prepaid_balance: customer.prepaid_balance,
          currency: customer.currency,
        });
        this.$set(this.saveSuccess, customer.user_id, true);
        setTimeout(() => this.$set(this.saveSuccess, customer.user_id, false), 2000);
      } catch (e) {
        this.$set(this.saveError, customer.user_id, e.response?.data?.message || 'Save failed.');
      } finally {
        this.saving = null;
      }
    },
  },
};
</script>

<style scoped>
.admin-customer-pricing {
  font-size: 16px;
}
.admin-customer-pricing >>> h3 { font-size: 22px; }
.admin-customer-pricing >>> th,
.admin-customer-pricing >>> td { font-size: 14px; }
.admin-customer-pricing >>> .btn { font-size: 13px; padding: 5px 12px; }
.admin-customer-pricing >>> input,
.admin-customer-pricing >>> select { font-size: 13px !important; height: 32px; }
.admin-customer-pricing >>> .spinner-border { width: 1.5rem; height: 1.5rem; }
</style>
